// src/encounter/ips-allergy-dialog.tsx
// Add/Edit dialog for the IPS-rail Allergies section. PRESENTATIONAL — composes DS Modal +
// Segmented (Drug / Non-drug) + CodeSearchField (allergen) + Select (criticality, reaction) +
// Button. No data hooks, no network: the host (the rail) supplies search results + master options +
// the submit/search callbacks,
// so this is Storybook- and test-friendly (mirrors FlagAddDialog). On submit it resolves the
// selection into an AllergyFormValue and hands it up; the rail turns that into a wire request
// via buildAllergyRequest + useSaveAllergy.
import { useMemo, useState } from 'react';
import {
  Button,
  Segmented,
  Select,
  TextField,
  CodeSearchField,
  HelperText,
  type CodeSearchResult,
  type SelectOption,
} from '@scripsteam/scrips-signal-ds';
import { AddSurface } from './add-surface';
import { DynamicDateInput } from '@/patients/components/chart/dynamic-date-input';
import {
  allergenKindOf,
  findCodingByOptionKey,
  findCodingOptionKey,
  codingOptionKey,
  type AllergenKind,
  type AllergyFormValue,
} from '@/encounter/lib/to-allergy-request';
import type { AllergyCodeOption, PatientAllergy } from '@/types/patient-chart-api';
import type { CodeWordCase } from '@/types/patient-api';

export interface IpsAllergyDialogProps {
  open: boolean;
  /** Present → Edit mode (pre-fill + "Save"); absent → Add mode ("Add"). */
  editing?: PatientAllergy | null;

  // Allergen search (host-owned; CodeSearchField is generic).
  /** Current search query text. */
  query: string;
  onQueryChange: (next: string) => void;
  /** Results for the current query in the CURRENT kind's source, already mapped to the DS shape. */
  results: CodeSearchResult[];
  searching?: boolean;
  /** Resolve a clicked result id → the full picker row. For a drug pick the host returns the
   *  medication with DRUG_ALLERGY_CATEGORY attached (`medicationToAllergen`); for a non-drug pick,
   *  the AllergyCode row with its own nested category. */
  resolveAllergen: (result: CodeSearchResult) => AllergyCodeOption | null;
  /**
   * PROD-1998. Tells the host which source to search: 'drug' → the org's medication catalogue
   * (SearchValueSets2?SearchFor=Medications), 'non-drug' → AllergyCode. Called when the Drug /
   * Non-drug toggle changes and when "Change" reopens the search in edit mode (the toggle is seeded
   * from the record's category there). The host starts in 'non-drug' and resets on close.
   */
  onKindChange?: (kind: AllergenKind) => void;

  // Criticality (severity) master options.
  criticalityOptions: CodeWordCase[];
  criticalityLoading?: boolean;

  /** Reaction master options (GET /api/Master/AllergenReaction). Flutter makes Reaction a
   *  MANDATORY dropdown on this form (`allergies_tab.dart:370-444`, fed by
   *  FetchAllergyReactionsEvent); the rail dialog previously hardcoded `reaction: null` on add, so
   *  the field could never be set from here. */
  reactionOptions: CodeWordCase[];
  reactionsLoading?: boolean;

  saving?: boolean;
  onSubmit: (value: AllergyFormValue) => void;
  onClose: () => void;
}

/** THE ruled field-label treatment (PROD-1801, Andrew 2 Aug) — shared, not re-declared.
 *  These six dialogs each carried a byte-identical local copy at xs/600/0.08em, which is a
 *  seventh idiom next to the four the rail popover already showed at once. The margin is the
 *  only local part: `AP_FIELD_LABEL` sets no spacing, and these labels sit above their field. */
// NOTE: AP_FIELD_LABEL lives in ap-dialog-label-style.tsx (out of scope for this pass — that file
// is not on the file list this conversion may touch), so its literal values (12/700/uppercase/
// 0.01em/--color-text-hint) are reproduced here as the equivalent class string rather than
// imported as a style object; values read from that file's exported constant to stay identical.
const FIELD_LABEL =
  'pa-ips-label block text-size-eyebrow font-bold uppercase tracking-field-label text-text-hint mb-1';
const FIELD_GROUP = 'mb-4';
const SELECTED_CHIP =
  'pa-ips-chip flex items-center justify-between gap-2 py-2 px-3 border border-solid border-border-default rounded-md bg-surface-card';

/** Outer gate + keyed remount. The inner form initialises its state from `editing` via lazy
 *  useState initialisers (no setState-in-effect). Re-keying on open/identifier guarantees a
 *  fresh form each time the dialog is shown for a different record. */
export function IpsAllergyDialog(props: IpsAllergyDialogProps) {
  if (!props.open) return null;
  return <IpsAllergyDialogForm key={props.editing?.identifier ?? 'add'} {...props} />;
}

function IpsAllergyDialogForm({
  editing = null,
  query,
  onQueryChange,
  results,
  searching,
  resolveAllergen,
  onKindChange,
  criticalityOptions,
  criticalityLoading,
  reactionOptions,
  reactionsLoading,
  saving,
  onSubmit,
  onClose,
}: IpsAllergyDialogProps) {
  const isEditing = !!editing?.identifier;
  // Drug / Non-drug (Flutter allergies_tab.dart:275-296). Seeded from the record on edit.
  const [kind, setKind] = useState<AllergenKind>(() => allergenKindOf(editing?.category));
  const [allergen, setAllergen] = useState<AllergyCodeOption | null>(() => {
    if (!editing?.code) return null;
    const cat = typeof editing.category === 'object' ? editing.category : null;
    return {
      code: editing.code.code ?? null,
      display: editing.code.display ?? null,
      system: editing.code.system ?? null,
      description: editing.code.description ?? null,
      shortName: editing.code.shortName ?? null,
      category: cat,
    };
  });
  // PROD-2735: Select keys are `system|code|display` (codingOptionKey) because the master lists
  // carry duplicate codes. `null` = untouched → derive the preselection from the record, matching
  // code+display first, so it still resolves when the options arrive after the form mounts.
  const [criticalityPick, setCriticalityPick] = useState<string | null>(null);
  const criticalityKey = criticalityPick ?? findCodingOptionKey(criticalityOptions, editing?.criticality);
  // Fields Flutter collects on this form that the rail dialog never did (allergies_tab.dart:256-708):
  //   Reaction    (:370-444, mandatory dropdown)
  //   Start Date  (:536-571, DynamicDatePicker -> free text like "3y" is valid)
  //   Description (:579-602, optional -> wire `note`)
  // All three already existed on AllergyFormValue and the wire; only the UI was missing, so an
  // allergy added from the rail silently stored null for each.
  const [reactionPick, setReactionPick] = useState<string | null>(null);
  const reactionKey = reactionPick ?? findCodingOptionKey(reactionOptions, editing?.reaction?.substance);
  // Prefer the stored VERBATIM display so "3y" round-trips as "3y" (same rule as the other
  // partial-date fields, PROD-1266).
  const [onsetDate, setOnsetDate] = useState(() => ({
    displayValue: editing?.onSetDisplay ?? '',
    isoValue: editing?.onset ?? null,
  }));
  const [note, setNote] = useState<string>(() => editing?.note ?? '');
  const [error, setError] = useState<string | null>(null);

  const reactionSelectOptions = useMemo<SelectOption[]>(
    () => reactionOptions.map((r) => ({ value: codingOptionKey(r), label: r.display ?? r.code ?? '—' })),
    [reactionOptions],
  );
  const criticalitySelectOptions = useMemo<SelectOption[]>(
    () => criticalityOptions.map((c) => ({ value: codingOptionKey(c), label: c.display ?? c.code ?? '—' })),
    [criticalityOptions],
  );

  function handleSubmit() {
    if (!allergen) {
      setError('Pick an allergen.');
      return;
    }
    // Typed-but-unreadable is a distinct error from empty — Flutter's grammar allows a duration, so
    // silently treating "3yy" as "no date" would lose the input with no explanation.
    if (!onsetDate.isoValue && onsetDate.displayValue.trim()) {
      setError('That start date could not be read. Try DD/MM/YYYY, MM/YYYY, YYYY, or a duration like 3Y.');
      return;
    }
    const criticality = findCodingByOptionKey(criticalityOptions, criticalityKey);
    const reaction = findCodingByOptionKey(reactionOptions, reactionKey);
    onSubmit({
      allergen,
      criticality,
      reaction,
      onset: onsetDate.isoValue,
      onSetDisplay: onsetDate.displayValue.trim() || null,
      note: note.trim() || null,
    });
  }

  return (
    <AddSurface
      open
      onClose={onClose}
      title={isEditing ? 'Edit allergy' : 'Add allergy'}
      size="md"
      commit={{
        run: handleSubmit,
        label: 'Save allergy',
        text: saving ? 'Saving…' : isEditing ? 'Save' : 'Add',
        busy: saving,
      }}
    >
      <div className="font-sans">
        {/* Drug / Non-drug — switching clears the pick (Flutter allergies_tab.dart:275-296). */}
        <div className={FIELD_GROUP}>
          <Segmented<AllergenKind>
            aria-label="Allergy type"
            value={kind}
            onChange={(next) => {
              if (saving || next === kind) return;
              setKind(next);
              setAllergen(null);
              setError(null);
              onQueryChange('');
              onKindChange?.(next);
            }}
            options={[
              { value: 'non-drug', label: 'Non-drug' },
              { value: 'drug', label: 'Drug' },
            ]}
          />
        </div>

        {/* Allergen */}
        <div className={FIELD_GROUP}>
          <span className={FIELD_LABEL}>Allergen</span>
          {allergen ? (
            <div className={SELECTED_CHIP}>
              <span className="text-size-sm text-text-primary">
                {allergen.display ?? allergen.code ?? '—'}
                {allergen.category?.display ? (
                  <span className="text-text-hint"> · {allergen.category.display}</span>
                ) : null}
              </span>
              <Button
                variant="tertiary"
                size="sm"
                onClick={() => {
                  setAllergen(null);
                  onKindChange?.(kind);
                }}
                disabled={saving}
              >
                Change
              </Button>
            </div>
          ) : (
            <CodeSearchField
              value={query}
              onChange={onQueryChange}
              results={results}
              loading={searching}
              placeholder={kind === 'drug' ? 'Search medications…' : 'Search allergens (e.g. peanuts, latex)…'}
              onResultClick={(r) => {
                const picked = resolveAllergen(r);
                if (picked) {
                  setAllergen(picked);
                  setError(null);
                }
              }}
            />
          )}
        </div>

        {/* Criticality / Severity */}
        <div className={FIELD_GROUP}>
          <Select
            label="Severity"
            options={criticalitySelectOptions}
            value={criticalityKey || undefined}
            onChange={setCriticalityPick}
            disabled={criticalityLoading || saving}
            placeholder={criticalityLoading ? 'Loading…' : 'Select severity'}
          />
        </div>

        {/* Reaction — Flutter allergies_tab.dart:370-444 */}
        <div className={FIELD_GROUP}>
          <Select
            label="Reaction"
            options={reactionSelectOptions}
            value={reactionKey || undefined}
            onChange={setReactionPick}
            disabled={reactionsLoading || saving}
            placeholder={reactionsLoading ? 'Loading…' : 'Select reaction'}
          />
        </div>

        {/* Start date — Flutter uses DynamicDatePicker here (allergies_tab.dart:542), so a
            duration like "3y" is a valid entry, not just a calendar date. */}
        <div className={FIELD_GROUP}>
          <DynamicDateInput
            label="Start date"
            displayValue={onsetDate.displayValue}
            isoValue={onsetDate.isoValue}
            onChange={setOnsetDate}
            disabled={saving}
          />
        </div>

        {/* Description — Flutter allergies_tab.dart:579-602, wire field `note` */}
        <div className={FIELD_GROUP}>
          <TextField
            label="Description"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional context…"
            disabled={saving}
          />
        </div>

        {error ? <HelperText tone="error">{error}</HelperText> : null}
      </div>
    </AddSurface>
  );
}
